import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Row, Col, Card, Button } from "reactstrap";
import ReactTable from "react-table";
import { connect } from "react-redux";
import fetchVariationChildService from "../../../../../Service/OPSServices/AmazonProductListing_v1/fetchVariationChildService";
import bulkUpdateVariationService from "../../../../../Service/OPSServices/AmazonProductListing_v1/bulkUpdateVariationService";

// Strict Select Cell — custom dropdown, works outside table overflow
function StrictSelectCell({ attr, initialVal, allowedValues, allowedLabels, asin, onAttrChange, submitError }) {
    const [localVal, setLocalVal] = useState(initialVal || "");
    const [isOpen, setIsOpen] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);

    const prevRef = useRef(initialVal);
    useEffect(() => {
        if (prevRef.current !== initialVal) {
            prevRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Close on page scroll, but not on dropdown internal scroll
    useEffect(() => {
        if (!isOpen) return;
        const handleScroll = (e) => {
            if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            setIsOpen(false);
        };
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, [isOpen]);

    const checkDropDirection = () => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setDropUp(window.innerHeight - rect.bottom < 250);
        }
    };

    const selectedLabel = allowedValues.reduce((found, opt, i) => {
        if (String(opt) === String(localVal)) return (allowedLabels && allowedLabels[i]) || String(opt);
        return found;
    }, null);

    return (
        <div ref={wrapperRef} style={{ width: "100%", position: "relative" }}>
            <div
                onClick={() => { checkDropDirection(); setIsOpen((prev) => !prev); }}
                style={{
                    fontSize: 12, borderRadius: 4, padding: "2px 24px 2px 6px",
                    width: "100%", border: submitError ? "1px solid #dc2626" : "1px solid #ccc",
                    background: "#fff", color: localVal ? "#111" : "#999", cursor: "pointer",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    userSelect: "none", minHeight: 24,
                }}
            >
                <span>{selectedLabel || localVal || "-- select --"}</span>
                <span style={{ fontSize: 10, color: "#666" }}>▼</span>
            </div>
            {submitError && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {submitError}
                </small>
            )}
            {isOpen && (
                <div ref={dropdownRef} style={{
                    position: "fixed", background: "#fff", border: "1px solid #d5d9d9",
                    borderRadius: 8, maxHeight: 220, overflowY: "auto", zIndex: 99999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)", minWidth: 150,
                    ...(dropUp
                        ? {
                            bottom: wrapperRef.current ? window.innerHeight - wrapperRef.current.getBoundingClientRect().top + 2 : "auto",
                            left: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().left : 0,
                        }
                        : {
                            top: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().bottom + 2 : "auto",
                            left: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().left : 0,
                        }
                    ),
                }}>
                    <div
                        onMouseDown={() => { setLocalVal(""); onAttrChange(asin, attr, ""); setIsOpen(false); }}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, color: "#999", background: localVal === "" ? "#e8f4fd" : "#fff" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = localVal === "" ? "#e8f4fd" : "#fff"; }}
                    >-- select --</div>
                    {allowedValues.map((opt, i) => {
                        const label = (allowedLabels && allowedLabels[i]) || String(opt);
                        const isSelected = String(opt) === String(localVal);
                        return (
                            <div
                                key={String(opt)}
                                onMouseDown={() => { setLocalVal(String(opt)); onAttrChange(asin, attr, String(opt)); setIsOpen(false); }}
                                style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, background: isSelected ? "#e8f4fd" : "#fff", color: "#111", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "#e8f4fd" : "#fff"; }}
                            >{label}</div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Suggestive input — free text with dropdown suggestions
function SuggestiveAttrInput({ attr, initialVal, allowedValues, deprecatedValues, maxLength, minLength, maxUtf8, asin, onAttrChange, submitError }) {
    const [localVal, setLocalVal] = useState(initialVal || "");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const prevInitialRef = useRef(initialVal);
    useEffect(() => {
        if (prevInitialRef.current !== initialVal) {
            prevInitialRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    // Close on page scroll, but not on dropdown internal scroll
    useEffect(() => {
        if (!showSuggestions) return;
        const handleScroll = (e) => {
            if (inputRef.current && inputRef.current.contains(e.target)) return;
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            setShowSuggestions(false);
        };
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, [showSuggestions]);

    const checkDropDirection = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropUp(window.innerHeight - rect.bottom < 250);
        }
    };

    // Real-time length validation
    const currentLen = localVal.length;
    const currentUtf8Len = new TextEncoder().encode(localVal).length;
    const isTooLong = (maxLength && currentLen > maxLength) || (maxUtf8 && !maxLength && currentUtf8Len > maxUtf8);
    const isBlank = localVal === "" && (initialVal !== "" && initialVal !== null && initialVal !== undefined);
    const isTooShort =
        minLength &&
        localVal.length > 0 &&
        localVal.length < minLength;
    // Real-time deprecated check
    const isDeprecated = deprecatedValues && deprecatedValues.length > 0 &&
        deprecatedValues.some((d) => String(d).toLowerCase() === String(localVal).toLowerCase());

    const allOptions = localVal && !allowedValues.includes(localVal) ? [localVal, ...allowedValues] : allowedValues;
    const filteredOptions = allOptions.filter(opt =>
        String(opt).toLowerCase().includes(String(localVal || "").toLowerCase())
    );

    return (
        <div style={{ width: "100%", position: "relative" }}>
            <input
                ref={inputRef}
                style={{
                    fontSize: 12, borderRadius: 4, padding: "2px 6px", width: "100%",
                    border: (isTooShort || isTooLong || submitError || isBlank) ? "1px solid #dc2626" : "1px solid #ccc",
                    background: "#fff", color: "#111"
                }}
                value={localVal}
                onFocus={() => { checkDropDirection(); setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onChange={(e) => { setLocalVal(e.target.value); onAttrChange(asin, attr, e.target.value); setShowSuggestions(true); }}
                placeholder={`Select ${attr}...`}
                autoComplete="off"
            />
            {isBlank && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    Cannot be blank
                </small>
            )}
            {isTooShort && (
                <small
                    style={{
                        color: "#dc2626",
                        fontSize: 11,
                        display: "block",
                        marginTop: 2,
                    }}
                >
                    Minimum {minLength} characters required
                </small>
            )}
            {isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {maxLength ? `Maximum ${maxLength} characters allowed` : `Maximum ${maxUtf8} UTF-8 bytes allowed`}
                </small>
            )}
            {isDeprecated && !isTooLong && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3, fontSize: 11, color: "#b45309", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 4, padding: "2px 6px" }}>
                    <span>⚠️</span>
                    <span>This value is <strong>deprecated</strong> by Amazon.</span>
                </div>
            )}
            {submitError && !isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {submitError}
                </small>
            )}
            {showSuggestions && filteredOptions.length > 0 && (
                <div ref={dropdownRef} style={{
                    position: "fixed",
                    ...(dropUp
                        ? {
                            bottom: inputRef.current ? window.innerHeight - inputRef.current.getBoundingClientRect().top + 2 : "auto",
                            left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                        }
                        : {
                            top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + 2 : "auto",
                            left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                        }
                    ),
                    width: inputRef.current ? inputRef.current.getBoundingClientRect().width : 150,
                    background: "#fff", border: "1px solid #d5d9d9", borderRadius: 8,
                    maxHeight: 220, overflowY: "auto", zIndex: 99999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}>
                    {filteredOptions.slice(0, 50).map((opt) => (
                        <div
                            key={String(opt)}
                            onMouseDown={() => { setLocalVal(String(opt)); onAttrChange(asin, attr, String(opt)); setShowSuggestions(false); }}
                            style={{ padding: "10px 12px", cursor: "pointer", fontSize: 12, background: "#fff", color: "#111" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                        >{String(opt)}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Plain text input cell
function EditableCell({ attr, initialVal, asin, onAttrChange, maxLength, maxUtf8, submitError, isInteger, minValue, minLength, maxValue, isNumber }) {
    const [localVal, setLocalVal] = useState(initialVal || "");

    const prevInitialRef = useRef(initialVal);
    useEffect(() => {
        if (prevInitialRef.current !== initialVal) {
            prevInitialRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    const currentLen = localVal.length;
    const currentUtf8Len = new TextEncoder().encode(localVal).length;
    const isTooLong = (maxLength && currentLen > maxLength) || (maxUtf8 && !maxLength && currentUtf8Len > maxUtf8);
    const isBlank = localVal === "" && (initialVal !== "" && initialVal !== null && initialVal !== undefined);
    const isTooShort =
        minLength &&
        localVal.length > 0 &&
        localVal.length < minLength;
    const numericVal = Number(localVal);
    const isIntegerInvalid = isInteger && localVal !== "" && (
        !Number.isInteger(numericVal) || isNaN(numericVal) ||
        (minValue !== null && numericVal < minValue) ||
        (maxValue !== null && numericVal > maxValue)
    );
    const isNumberInvalid = isNumber && localVal !== "" && (
        isNaN(numericVal) ||
        (minValue !== null && numericVal < minValue) ||
        (maxValue !== null && numericVal > maxValue)
    );

    const hasError = isTooShort || isTooLong || isBlank || isIntegerInvalid || isNumberInvalid || submitError;

    return (
        <div style={{ width: "100%" }}>
            <input
                type="text"
                value={localVal}
                onChange={(e) => {
                    let newVal = e.target.value;
                    // integer → whole numbers allowed
                    if (isInteger && newVal !== "" && !/^\d*$/.test(newVal)) return;
                    //  number → decimal allowed
                    if (isNumber && newVal !== "" && !/^\d*\.?\d*$/.test(newVal)) return;
                    setLocalVal(newVal);
                    onAttrChange(asin, attr, newVal);
                }}
                style={{
                    fontSize: 12, borderRadius: 4, padding: "2px 6px", width: "100%",
                    border: hasError ? "1px solid #dc2626" : "1px solid #ccc",
                    color: "#111",
                    background: "#fff",
                }}
                placeholder={`Enter ${attr}`}
            />
            {isBlank && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    Cannot be blank
                </small>
            )}
            {isTooShort && (
                <small
                    style={{
                        color: "#dc2626",
                        fontSize: 11,
                        display: "block",
                        marginTop: 2,
                    }}
                >
                    Minimum {minLength} characters required
                </small>
            )}
            {isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {maxLength ? `Maximum ${maxLength} characters allowed` : `Maximum ${maxUtf8} UTF-8 bytes allowed`}
                </small>
            )}
            {/* integer error */}
            {isIntegerInvalid && !isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {!Number.isInteger(numericVal) || isNaN(numericVal)
                        ? "Must be a whole number"
                        : minValue !== null && numericVal < minValue
                            ? `Minimum value is ${minValue}`
                            : `Maximum value is ${maxValue}`}
                </small>
            )}
            {/* number error */}
            {isNumberInvalid && !isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {isNaN(numericVal)
                        ? "Must be a valid number"
                        : minValue !== null && numericVal < minValue
                            ? `Minimum value is ${minValue}`
                            : `Maximum value is ${maxValue}`}
                </small>
            )}
            {submitError && !isTooLong && !isIntegerInvalid && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {submitError}
                </small>
            )}
            {isInteger && (minValue !== null || maxValue !== null) && !isIntegerInvalid && localVal !== "" && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                    <small style={{ color: "#6b7280", fontSize: 11 }}>
                        {minValue !== null && maxValue !== null && `${minValue} – ${maxValue}`}
                        {minValue !== null && maxValue === null && `Min: ${minValue}`}
                        {minValue === null && maxValue !== null && `Max: ${maxValue}`}
                    </small>
                </div>
            )}
        </div>
    );
}

// Confirm modal — shows edited rows before final update
function ConfirmUpdateModal({ editedRows, varAttrs, onConfirm, onCancel, submitting }) {
    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: "#fff", borderRadius: 10, width: 560, maxWidth: "95vw", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #e5e7eb" }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "#111" }}>
                        Confirm Update — {editedRows.length} product{editedRows.length > 1 ? "s" : ""}
                    </span>
                    <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#6b7280", lineHeight: 1 }}>×</button>
                </div>
                <div style={{ overflowY: "auto", flex: 1, padding: "12px 18px" }}>
                    <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                        The following child products will be updated. Once confirmed, changes will be patched to Amazon.
                    </p>
                    {editedRows.map((row) => (
                        <div key={`${row.asin}__${row.sku || "nosku"}`} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", marginBottom: 8, background: "#f9fafb" }}>
                            <div style={{ marginBottom: 6 }}>
                                <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#1a6bb5" }}>
                                    {row.asin}
                                </span>
                                {row.sku && (
                                    <span style={{ marginLeft: 8, fontFamily: "monospace", fontSize: 11, background: "#f3f4f6", border: "1px solid #dde1e7", borderRadius: 4, padding: "1px 6px", color: "#374151" }}>
                                        {row.sku}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {varAttrs.map((attr) => {
                                    const val = row.editedAttrs[attr];
                                    if (val === undefined || val === null) return null;
                                    return (
                                        <span key={attr} style={{ fontSize: 11, background: "#e8f4fd", border: "1px solid #b3d7f5", borderRadius: 4, padding: "2px 8px", color: "#1a6bb5" }}>
                                            <b>{attr}:</b> {String(val) || <i style={{ color: "#999" }}>empty</i>}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ padding: "12px 18px 19px 18px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                        className="no-btn-background-color mr-3"
                        onClick={onCancel}
                        disabled={submitting}
                    >Cancel</Button>
                    <Button
                        className="btn-background-color"
                        // onClick={onConfirm}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                                Updating...
                            </>
                        ) : "Update Listing"}
                    </Button>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

function VariationsChildGrid(props) {
    const [variationData, setVariationData] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editedAttrs, setEditedAttrs] = useState({});
    const [submitErrors, setSubmitErrors] = useState({});
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const TITLE_MAX_LENGTH = 75; // Amazon new title policy

    useEffect(() => {
        if (!props.selectedAsin) return;
        setLoading(true);
        setError(null);
        setEditedAttrs({});
        setSubmitErrors({});
        setSuccessMsg("");

        const data = {
            integerId: props.integerId,
            asin: props.selectedAsin,
            sku: props.sku || "",
            variation_theme: props.prefetchedVariationTheme || null,
        };
        fetchVariationChildService(props.token, data).then((res) => {
            if (res && res.status) {
                setVariationData(res);
            } else {
                setError((res && res.message) || "Failed to load variation data.");
            }
            setLoading(false);
        });
    }, [props.selectedAsin, props.sku, props.prefetchedVariationTheme]);

    const editedAttrsRef = useRef(editedAttrs);
    useEffect(() => {
        editedAttrsRef.current = editedAttrs;
    }, [editedAttrs]);

    const handleAttrChange = useCallback((asinSkuKey, attrKey, value) => {
        setEditedAttrs((prev) => ({
            ...prev,
            [asinSkuKey]: { ...(prev[asinSkuKey] || {}), [attrKey]: value },
        }));
        setSubmitErrors((prev) => {
            if (!prev[asinSkuKey] || !prev[asinSkuKey][attrKey]) return prev;
            const updated = { ...prev, [asinSkuKey]: { ...prev[asinSkuKey] } };
            delete updated[asinSkuKey][attrKey];
            return updated;
        });
    }, []);

    // Resolve field meta from schema — field type, validation rules
    const getAttrFieldMeta = useCallback((attrKey) => {
        const empty = {
            allowedValues: [], allowedLabels: [], isSuggestive: false,
            isStrictSelect: false, maxLength: null, maxUtf8: null,
            deprecatedValues: [], isRequired: false,
            isInteger: false, minValue: null, maxValue: null, minLength: null,
        };
        if (!props.schemaFields) return empty;

        const attrLower = attrKey.toLowerCase();

        // Normalize: strip trailing _name for fuzzy matching
        const stripName = (s) => s.replace(/_name$/, "").replace(/^name_/, "");

        // 1. Exact match
        let field = props.schemaFields.find(
            (f) => f.field === attrKey || f.field === attrLower
        );

        // 2. Normalized _name-strip match (color_name ↔ color, size_name ↔ size)
        if (!field) {
            const attrStripped = stripName(attrLower);
            field = props.schemaFields.find((f) => {
                const fLower = (f.field || "").toLowerCase();
                return stripName(fLower) === attrStripped;
            });
        }

        // 3. Label-based match ("Color Name" → "color_name" → strip → "color")
        if (!field) {
            const labelNormalized = (s) =>
                (s || "").toLowerCase().trim().replace(/\s+/g, "_");
            field = props.schemaFields.find((f) => {
                const fLabel = labelNormalized(f.label);
                return fLabel === attrLower || stripName(fLabel) === stripName(attrLower);
            });
        }

        // 4. Prefix/suffix partial match (last resort)
        if (!field) {
            const attrTokens = attrLower.split("_").filter(Boolean);
            field = props.schemaFields.find((f) => {
                const fLower = (f.field || "").toLowerCase();
                const fTokens = fLower.split("_").filter(Boolean);
                if (fTokens.length !== attrTokens.length + 1) return false;
                const suffixMatch = fTokens.slice(-attrTokens.length).join("_") === attrLower;
                const prefixMatch = fTokens.slice(0, attrTokens.length).join("_") === attrLower;
                return suffixMatch || prefixMatch;
            });
        }

        if (!field) return empty;

        const options = (field.schema && field.schema.allowed_values) || [];
        const allowCustom = field.schema && field.schema.rules && field.schema.rules.allow_custom_values;
        const isDropdown = field.render && field.render.component === "SELECT";
        const rules = (field.schema && field.schema.rules) || {};

        return {
            allowedValues: options,
            allowedLabels: (field.schema && field.schema.allowed_value_labels) || [],
            isSuggestive: options.length > 0 && allowCustom === true,
            isStrictSelect: isDropdown && options.length > 0 && allowCustom !== true,
            maxLength: attrKey === "item_name" ? TITLE_MAX_LENGTH : (rules.maxLength || null),
            minLength: rules.minLength || null,
            maxUtf8: rules.maxUtf8ByteLength || null,
            deprecatedValues: (field.schema && field.schema.deprecated_values) || [],
            isRequired: rules.required === true,
            isInteger: rules.type === "integer",
            isNumber: rules.type === "number",
            minValue: rules.minimum !== undefined ? rules.minimum : null,
            maxValue: rules.maximum !== undefined ? rules.maximum : null,
        };
    }, [props.schemaFields]);

    const children = ((variationData && variationData.children) || []).filter((c) => !c.is_parent);
    const varAttrs = (variationData && variationData.variation_attributes) || [];
    const theme = (variationData && variationData.variation_theme) || "";
    const uniqueChildCount = [
        ...new Set(children.map(item => item.asin))
    ].length;
    const filteredChildren = children.filter((row) => {
        const text = searchText.toLowerCase();
        const asin = (row.asin || "").toLowerCase();
        const title = (row.title || "").toLowerCase();
        const skus = (row.skus || []).join(" ").toLowerCase();
        const attrs = Object.values(row.attributes || {}).join(" ").toLowerCase();
        return asin.includes(text) || title.includes(text) || skus.includes(text) || attrs.includes(text);
    });

    // Validate all rows on submit click
    const handleSubmitClick = useCallback(() => {
        const errors = {};
        children.forEach((row) => {
            const asinSkuKey = `${row.asin}__${row.sku || "nosku"}`;
            varAttrs.forEach((attr) => {
                const meta = getAttrFieldMeta(attr);
                const val = (editedAttrs[asinSkuKey] && editedAttrs[asinSkuKey][attr] !== undefined)
                    ? editedAttrs[asinSkuKey][attr]
                    : (row.attributes && row.attributes[attr]) || "";
                const valStr = String(val || "").trim();

                if (meta.isRequired && !valStr) {
                    if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                    errors[asinSkuKey][attr] = "This field is required";
                    return;
                }
                if (meta.minLength && valStr.length > 0 && valStr.length < meta.minLength) {
                    if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                    errors[asinSkuKey][attr] =
                        `Minimum ${meta.minLength} characters required`;
                    return;
                }
                if (meta.maxLength && valStr.length > meta.maxLength) {
                    if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                    errors[asinSkuKey][attr] = `Maximum ${meta.maxLength} characters allowed`;
                    return;
                }
                if (meta.maxUtf8 && !meta.maxLength) {
                    const utf8len = new TextEncoder().encode(valStr).length;
                    if (utf8len > meta.maxUtf8) {
                        if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                        errors[asinSkuKey][attr] = `Maximum ${meta.maxUtf8} UTF-8 bytes allowed`;
                        return;
                    }
                }
                // integer check (whole number only)
                if (meta.isInteger && valStr !== "") {
                    const numVal = Number(valStr);
                    if (!Number.isInteger(numVal) || isNaN(numVal)) {
                        if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                        errors[asinSkuKey][attr] = "Must be a whole number";
                        return;
                    }
                    if (meta.minValue !== null && numVal < meta.minValue) {
                        if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                        errors[asinSkuKey][attr] = `Minimum value is ${meta.minValue}`;
                        return;
                    }
                    if (meta.maxValue !== null && numVal > meta.maxValue) {
                        if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                        errors[asinSkuKey][attr] = `Maximum value is ${meta.maxValue}`;
                        return;
                    }
                }

                // number check (decimal allowed)
                if (meta.isNumber && valStr !== "") {
                    const numVal = Number(valStr);
                    if (isNaN(numVal)) {
                        if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                        errors[asinSkuKey][attr] = "Must be a valid number";
                        return;
                    }
                    if (meta.minValue !== null && numVal < meta.minValue) {
                        if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                        errors[asinSkuKey][attr] = `Minimum value is ${meta.minValue}`;
                        return;
                    }
                    if (meta.maxValue !== null && numVal > meta.maxValue) {
                        if (!errors[asinSkuKey]) errors[asinSkuKey] = {};
                        errors[asinSkuKey][attr] = `Maximum value is ${meta.maxValue}`;
                        return;
                    }
                }
            });
        });
        if (Object.keys(errors).length > 0) {
            setSubmitErrors(errors);
            return;
        }
        setSubmitErrors({});
        setShowConfirm(true);
    }, [children, varAttrs, editedAttrs, getAttrFieldMeta]);

    // Only edited rows for confirm modal
    const editedRows = useMemo(() => {
        return children
            .filter((row) => {
                const asinSkuKey = `${row.asin}__${row.sku || "nosku"}`;
                const edits = editedAttrs[asinSkuKey];
                if (!edits) return false;
                return Object.entries(edits).some(([attr, value]) => {
                    const originalValue = (row.attributes && row.attributes[attr]) || "";
                    return String(value || "") !== String(originalValue || "");
                });
            })
            .map((row) => {
                const asinSkuKey = `${row.asin}__${row.sku || "nosku"}`;
                const edits = editedAttrs[asinSkuKey];
                const changedAttrs = {};
                Object.entries(edits).forEach(([attr, value]) => {
                    const originalValue = (row.attributes && row.attributes[attr]) || "";
                    if (String(value || "") !== String(originalValue || "")) {
                        changedAttrs[attr] = value;
                    }
                });
                return { asin: row.asin, sku: row.sku, editedAttrs: changedAttrs };
            });
    }, [children, editedAttrs]);
    // Call bulk update service after confirm
    const handleConfirmUpdate = useCallback(async () => {
        setSubmitting(true);
        try {
            const payload = children
                .filter((row) => {
                    const asinSkuKey = `${row.asin}__${row.sku || "nosku"}`;
                    const edits = editedAttrs[asinSkuKey];
                    if (!edits) return false;
                    return Object.entries(edits).some(([attr, value]) => {
                        const originalValue = (row.attributes && row.attributes[attr]) || "";
                        return String(value || "") !== String(originalValue || "");
                    });
                })
                .map((row) => {
                    const asinSkuKey = `${row.asin}__${row.sku || "nosku"}`;
                    return {
                        integerId: props.integerId,
                        asin: row.asin,
                        skus: row.sku ? [row.sku] : (row.skus || []),
                        attributes: { ...editedAttrs[asinSkuKey] },
                        oldValues: Object.keys(editedAttrs[asinSkuKey] || {}).reduce((acc, attr) => {
                            acc[attr] = (row.attributes && row.attributes[attr] !== undefined) ? row.attributes[attr] : null;
                            return acc;
                        }, {}),
                        selectedAsin: props.selectedAsin,
                        selectedSku: props.sku || ""
                    };
                });

            const res = await bulkUpdateVariationService(props.token, payload);

            if (!res || !res.status) {
                setError(res.message || "Update failed. Please try again.");
                setShowConfirm(false);
                return;
            }

            setShowConfirm(false);
            setVariationData((prev) => {
                if (!prev) return prev;
                const updatedChildren = prev.children.map((child) => {
                    const asinSkuKey = `${child.asin}__${child.sku || "nosku"}`;
                    const edits = editedAttrs[asinSkuKey];
                    if (!edits) return child;
                    return {
                        ...child,
                        attributes: { ...child.attributes, ...edits },
                    };
                });
                return { ...prev, children: updatedChildren };
            });

            setEditedAttrs({});
            setSuccessMsg(`Feed submitted for ${payload.length} product${payload.length > 1 ? "s" : ""}. Amazon is processing the update.`);
            setTimeout(() => setSuccessMsg(""), 8000);
        } catch (err) {
            setError("Update failed. Please try again.");
            setShowConfirm(false);
        } finally {
            setSubmitting(false);
        }
    }, [children, editedAttrs, props.token]);
    const columns = useMemo(() => [
        {
            Header: "ASIN",
            accessor: "asin",
            width: 150,
            Cell: (cellInfo) => (
                <div className="d-flex justify-content-center w-100">
                    <p className="mb-0">
                        {cellInfo.value}
                    </p>
                </div>
            ),
        },
        {
            Header: "SKU",
            accessor: "sku",
            width: 250,
            Cell: (cellInfo) => (
                <div className="d-flex w-100">
                    {cellInfo.value ? (
                        <span>
                            {cellInfo.value}
                        </span>
                    ) : (
                        <p className="mb-0 text-muted">—</p>
                    )}
                </div>
            ),
        },
        {
            Header: "Product Title",
            accessor: "title",
            minWidth: 600,
            Cell: (cellInfo) => (
                <div className="d-flex justify-content-start w-100">
                    <p className="mb-0"
                        title={cellInfo.value || ""}
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%"
                        }}>{cellInfo.value || "—"}</p>
                </div>
            ),
        },
        ...varAttrs.map((attr) => ({
            Header: attr.charAt(0).toUpperCase() + attr.slice(1).replace(/_/g, " "),
            accessor: attr,
            width: 180,
            Cell: (cellInfo) => {
                const asinSkuKey = `${cellInfo.original.asin}__${cellInfo.original.sku || "nosku"}`;
                const currentVal = (editedAttrsRef.current[asinSkuKey] && editedAttrsRef.current[asinSkuKey][attr] !== undefined)
                    ? editedAttrsRef.current[asinSkuKey][attr]
                    : (cellInfo.original.attributes && cellInfo.original.attributes[attr]) || "";
                const { allowedValues, allowedLabels, isSuggestive, isStrictSelect, maxLength, minLength, maxUtf8, deprecatedValues, isInteger, isNumber, minValue, maxValue } = getAttrFieldMeta(attr);
                const submitError = submitErrors[asinSkuKey] && submitErrors[asinSkuKey][attr];

                return (
                    <div className="d-flex justify-content-center w-100">
                        {isStrictSelect ? (
                            <StrictSelectCell
                                key={`${asinSkuKey}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                allowedValues={allowedValues}
                                allowedLabels={allowedLabels}
                                asin={asinSkuKey}
                                onAttrChange={handleAttrChange}
                                submitError={submitError}
                            />
                        ) : isSuggestive ? (
                            <SuggestiveAttrInput
                                key={`${asinSkuKey}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                allowedValues={allowedValues}
                                deprecatedValues={deprecatedValues}
                                minLength={minLength}
                                maxLength={maxLength}
                                maxUtf8={maxUtf8}
                                asin={asinSkuKey}
                                onAttrChange={handleAttrChange}
                                submitError={submitError}
                            />
                        ) : (
                            <EditableCell
                                key={`${asinSkuKey}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                asin={asinSkuKey}
                                onAttrChange={handleAttrChange}
                                minLength={minLength}
                                maxLength={maxLength}
                                maxUtf8={maxUtf8}
                                isInteger={isInteger}
                                isNumber={isNumber}
                                minValue={minValue}
                                maxValue={maxValue}
                                submitError={submitError}
                            />
                        )}
                    </div>
                );
            },
        })),
    ], [varAttrs, getAttrFieldMeta, handleAttrChange, submitErrors]);



    const hasEdits = children.some((row) => {
        const asinSkuKey = `${row.asin}__${row.sku || "nosku"}`;
        const edits = editedAttrs[asinSkuKey];

        if (!edits) return false;

        return Object.entries(edits).some(([attr, value]) => {
            const originalValue =
                (row.attributes && row.attributes[attr]) || "";

            return String(value || "") !== String(originalValue || "");
        });
    });
    const totalErrors = Object.values(submitErrors).reduce((sum, obj) => sum + Object.keys(obj).length, 0);
    const hasBlankEdits = children.some((row) => {
        const asinSkuKey = `${row.asin}__${row.sku || "nosku"}`;
        const edits = editedAttrs[asinSkuKey];
        if (!edits) return false;
        return Object.entries(edits).some(([attr, val]) => {
            const originalVal = (row.attributes && row.attributes[attr]) || "";
            return val === "" && originalVal !== "";
        });
    });
    return (
        <Card className="card-of-problemarea p-3 mb-4 mt-4 shadow-sm border-0">
            <Row className="mb-3 align-items-center">
                <Col md={7}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <h4 className="listing-page-title mb-0" style={{ fontWeight: "bold" }}>
                        Variation Family
                        </h4>
                        {/* {!props.isParentProduct&&(
                        <>
                        <span className="variation-badge">
                            Theme :&nbsp;<b style={{ color: "#1a6bb5" }}>{theme}</b>
                        </span>
                        {variationData && variationData.parent_asin && (
                            <span className="variation-badge">
                                Parent ASIN :&nbsp;
                                <b style={{ fontFamily: "monospace", color: "#1a6bb5" }}>{variationData.parent_asin}</b>
                            </span>
                        )}
                        <span className="variation-badge">
                            Child :&nbsp;<b>{uniqueChildCount}</b>
                        </span>
                        </>)} */}
                    </div>
                </Col>
                <Col md={5} className="d-flex justify-content-end align-items-center" style={{ gap: 8 }}>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="form-control w-50"
                    />
                    <Button
                        onClick={handleSubmitClick}
                        disabled={!hasEdits || submitting || hasBlankEdits}
                        className="btn-background-color-Proceed-gallery"
                    >
                        Submit
                    </Button>
                </Col>
            </Row>

            {totalErrors > 0 && (
                <div style={{ background: "#fff3f3", border: "1px solid #ffb3b3", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#cc0000", display: "flex", alignItems: "center", gap: 6 }}>
                    ⚠️ <b>{totalErrors} validation error{totalErrors > 1 ? "s" : ""} found.</b> Please fix the highlighted fields.
                </div>
            )}
            {error && (
                <div style={{ background: "#fff3f3", border: "1px solid #ffb3b3", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#cc0000", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>❌ {error}</span>
                    <button
                        onClick={() => setError(null)}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#cc0000" }}
                    >×</button>
                </div>
            )}

            {hasBlankEdits && (
                <div style={{ background: "#fff9eb", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#92400e", display: "flex", alignItems: "center", gap: 6 }}>
                    ⚠️ <b>Blank values found.</b> Cannot submit.
                </div>
            )}
            {successMsg && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#166534", display: "flex", alignItems: "center", gap: 6 }}>
                    ✅ {successMsg}
                </div>
            )}

            <Row>
                <Col md={12}>
                    <ReactTable
                        data={filteredChildren}
                        columns={columns}
                        loading={loading}
                        className="-highlight -striped"
                        getTheadProps={() => ({
                            style: { overflowY: "hidden", background: "#B1FA63", color: "#243837" },
                        })}
                        getTrProps={(state, rowInfo) => {
                            if (!rowInfo) return {};
                            const asinSkuKey = `${rowInfo.original.asin}__${rowInfo.original.sku || "nosku"}`;
                            const hasRowError = submitErrors[asinSkuKey] && Object.keys(submitErrors[asinSkuKey]).length > 0;
                            return {
                                style: {
                                    background: hasRowError ? "#fff5f5" : rowInfo.original.is_parent ? "#fffbeb" : undefined,
                                    fontStyle: rowInfo.original.is_parent ? "italic" : "normal",
                                    overflow: "visible",
                                },
                            };
                        }}
                        defaultPageSize={5}
                        noDataText={loading ? "Loading..." : "No Data Found."}
                    />
                </Col>
            </Row>

            {showConfirm && (
                <ConfirmUpdateModal
                    editedRows={editedRows}
                    varAttrs={varAttrs}
                    onConfirm={handleConfirmUpdate}
                    onCancel={() => setShowConfirm(false)}
                    submitting={submitting}
                />
            )}
        </Card>
    );
}

const mapStateToProps = (state) => ({
    token: state.loggedUser.token,
});

export default connect(mapStateToProps)(VariationsChildGrid);